import { useState, useEffect } from "react";
import { supabase } from "@/integrations/supabase/client";

interface AppSettings {
  isBeta: boolean;
  isProduction: boolean;
  appVersion: string;
  maintenanceMode: boolean;
  maintenanceMessage: string;
  instagramUrl: string;
  instagramVisible: boolean;
}

const defaults: AppSettings = {
  isBeta: true,
  isProduction: false,
  appVersion: "0.1.0",
  maintenanceMode: false,
  maintenanceMessage: "",
  instagramUrl: "https://www.instagram.com/drumlog.pro",
  instagramVisible: false,
};

let cached: AppSettings | null = null;
let fetchPromise: Promise<AppSettings> | null = null;

function fetchSettings(): Promise<AppSettings> {
  if (fetchPromise) return fetchPromise;
  fetchPromise = Promise.resolve(supabase
    .from("app_settings")
    .select("*")
    .eq("id", "00000000-0000-0000-0000-000000000001")
    .single()
  ).then(({ data }) => {
      const d = data as any;
      const result: AppSettings = d
        ? {
            isBeta: d.is_beta ?? defaults.isBeta,
            isProduction: d.is_production ?? defaults.isProduction,
            appVersion: d.app_version ?? defaults.appVersion,
            maintenanceMode: d.maintenance_mode,
            maintenanceMessage: d.maintenance_message ?? "",
            instagramUrl: d.social_instagram_url ?? defaults.instagramUrl,
            instagramVisible: d.social_instagram_visible ?? defaults.instagramVisible,
          }
        : defaults;
      cached = result;
      setTimeout(() => { fetchPromise = null; }, 30000);
      return result;
    });
  return fetchPromise;
}

export function useAppSettings() {
  const [settings, setSettings] = useState<AppSettings>(cached ?? defaults);

  useEffect(() => {
    if (cached) {
      setSettings(cached);
    }
    fetchSettings().then(setSettings);
  }, []);

  return settings;
}
